// ChatTab.tsx
import { CHEAP_TEXT_MODEL, MAIN_TEXT_MODEL } from '#lib/gptBackend';
import React, { useCallback, useRef, useEffect, useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import GenericImageInput, { GenericImageInputHandle } from './GenericImageInput';
import { clientAccessSuspension, SUSPENSION_MESSAGE } from '#lib/access-suspension';
import { Plus, ChevronDown, Cpu, Lock } from '@/components/ui/lucide-memo';
import { useTablesStore } from '@/components/tables/tablesStore';
import { Paperclip, Sparkles, X } from '@/components/ui/lucide-memo';
import { Chat } from './Chat';
import { ChatStateItem, LeasingOrganisationDTO, INSTRUCTION_MODEL_IDS, LockableFile, AGENT_LLMS } from  '#types/types';
import VoiceInputButton from './VoiceInputButton';
import ChatActionButton from './ChatActionButton';
import { StudioModelsPopover } from './StudioModelsPopover';
import PromptReferenceTagCards, { hasPromptReferenceTokens } from './PromptReferenceTagCards';
import { modelMappings } from '@/components/ModelSelector';
import {
  AGENT_MODEL_REGISTRY,
  filterAgentModelIdsForContext,
  ANALYST_TRIAL_MODEL,
  MODEL_POWER_HINTS as STUDIO_MODEL_POWER_HINTS,
  shouldUnlockIncompatibleStudioModelPin,
} from '#lib/model-registry';
import { scissorsCursor } from '#lib/svg';
import { useKernel } from '@/kernel/Kernel';
import {
  useKernelStore,
} from '@/kernel/kernelStores';
import {
  resolveProjectIdFromChatSessionContext,
} from '#lib/leasing/chat-session-project-focus';
import { SlidesReviewLauncher } from './slides/SlidesReviewLauncher';
import { PlanSendToSlides } from './footfall/PlanSendToSlides';

import { SessionRow } from './portal/SessionRow';
import { SourceModeChip } from './portal/SourceModeChip';
import { resolveSourceAgentMode } from '#lib/leasing/sourceAgentMode';
import { SourceResumeChip } from './portal/SourceResumeChip';
import { matchSourceSlashCommands, type SourceSlashCommand } from './source/sourceSlashCommands';
import { matchListSlashCommands } from './list/listSlashCommands';
import { matchPlanSlashCommands } from './footfall/planSlashCommands';
import { PULSE_COMMAND_DEFINITIONS } from './footfall/pulseCommands';

/** The menu shows several halves; each says whose commands it is listing. */
const SLASH_GROUP_LABELS: Record<'source' | 'agent' | 'list' | 'plan' | 'designer', string> = {
  source: 'Source commands',
  agent: 'Agent actions',
  list: 'List commands',
  plan: 'Merch commands',
  designer: 'Designer commands',
};
import { useBrandMention } from './source/useBrandMention';
import { useFactoryArchetypeMention } from './list/useFactoryArchetypeMention';
import { ListPromptEditor } from './list/ListPromptEditor';
import type { ListPromptEditorHandle } from '#types/types';
import { buildAgentActionMenu } from '@/components/source/sourceSlashCommands';
import { SlashCommandMenu } from '@/components/ui/SlashCommandMenu';
import { designerBrainOfSession } from '#lib/designer/brains';
import { matchDesignerSlashCommands } from './designer/designerSlashCommands';
import { DEALER_COMMAND_DEFINITIONS } from '@/components/dealerCommands';
import { useSessionMessages } from './portal/useSessionMessages';
import { agentIsWorking } from '#lib/chat/agentIsWorking';
import { ListModeChip } from '@/components/list/ListModeChip';
import { DesignerBudgetChip } from '@/components/designer/DesignerBudgetChip';
import { SourceBudgetProposalCard } from '@/components/source/SourceBudgetProposalCard';
import { readSourceBudgetProposal } from '#lib/leasing/sourceBudgetProposal';
import { ListPermissionPrompt, selectListPermission } from '@/components/list/ListPermissionPrompt';

const MemoizedPlus = React.memo(Plus);
// Module constants, not literals in the JSX: GenericImageInput is React.memo and the composer
// re-renders on every keystroke, so a fresh object here would re-render the preview strip
// on each character typed.
const IMAGE_INPUT_STYLE_WITH_PREVIEWS: React.CSSProperties = { paddingTop: '8px' };
const IMAGE_INPUT_STYLE_EMPTY: React.CSSProperties = {};
const AGENT_MODEL_LABELS: Record<string, string> = Object.values(AGENT_MODEL_REGISTRY).reduce((acc, def) => {
  acc[def.id] = def.name;
  return acc;
}, {} as Record<string, string>);
const getModelDisplayName = (modelId: string): string => {
  return AGENT_MODEL_LABELS[modelId] || modelMappings[modelId]?.name || modelId;
};
type ModelProvider = 'nano' | 'openai' | 'gemini' | 'anthropic' | 'moonshot' | 'meta' | 'xai' | 'bfl' | 'qwen' | 'seed' | 'ideogram' | 'unknown';
const getModelProvider = (modelId: string): ModelProvider => {
  const id = modelId.toLowerCase();
  if (id.startsWith('nano-banana')) return 'nano';
  if (id.startsWith('gpt-') || id.startsWith('o3') || id.startsWith('o4')) return 'openai';
  if (id.startsWith('gemini')) return 'gemini';
  if (id.startsWith('claude')) return 'anthropic';
  if (id.startsWith('kimi')) return 'moonshot';
  if (id.startsWith('muse')) return 'meta';
  if (id.startsWith('grok')) return 'xai';
  if (id.startsWith('flux') || id.startsWith('fx-fill')) return 'bfl';
  if (id.startsWith('qwen')) return 'qwen';
  if (id.startsWith('seedream') || id.startsWith('seedance')) return 'seed';
  if (id.startsWith('ideogram')) return 'ideogram';
  return 'unknown';
};
// Only agent LLM ids reach ProviderModelIcon (every call site is agent-scoped, and
// AGENT_MODEL_REGISTRY only yields openai / gemini / anthropic / moonshot), so those four
// are the entries that actually render. Kimi was the one falling through to the sparkle.
// The nano / xai / bfl / qwen paths below are unreachable today AND point at files that
// were never merged (they live only on origin/ui-fixes-studio); wire the assets in before
// making any of those providers reachable.
const PROVIDER_LOGO_SRC: Partial<Record<ModelProvider, string>> = {
  nano: '/providers/nanobanana.svg',
  openai: '/providers/openai.svg',
  gemini: '/providers/gemini.svg',
  anthropic: '/providers/anthropic.svg',
  moonshot: '/providers/moonshot.png',
  xai: '/providers/grok.svg',
  bfl: '/providers/bfl.svg',
  qwen: '/providers/qwen.svg',
};
const ProviderModelIcon: React.FC<{ modelId: string; size?: number }> = React.memo(({ modelId, size = 14 }) => {
  const provider = getModelProvider(modelId);
  // Track WHICH src failed, not a bare boolean: this icon sits at a fixed position in the
  // composer and is reused across model switches, so a sticky flag would suppress every
  // later model's logo after a single 404.
  const [failedSrc, setFailedSrc] = useState<string | null>(null);
  const logoSrc = PROVIDER_LOGO_SRC[provider];
  if (logoSrc && failedSrc !== logoSrc) {
    const monochromeFilter = 'brightness(0) saturate(0) invert(0.88)';
    return (
      <img
        src={logoSrc}
        alt={`${provider} logo`}
        className="object-contain opacity-90"
        style={{ width: size, height: size, filter: monochromeFilter }}
        loading="lazy"
        onError={() => setFailedSrc(logoSrc)}
      />
    );
  }
  return <Sparkles size={size} strokeWidth={1.8} className="text-[#d8d7d3] opacity-90" />;
});
ProviderModelIcon.displayName = 'ProviderModelIcon';

const MODEL_POWER_HINTS: Record<string, string> = {
  [MAIN_TEXT_MODEL]: 'Main reasoning model',
  [CHEAP_TEXT_MODEL]: 'Fast and cheap',
  ...STUDIO_MODEL_POWER_HINTS,
};
const PROVIDER_POWER_HINTS: Record<ModelProvider, string> = {
  nano: 'Direct image editing',
  openai: 'Strong general reasoning',
  gemini: 'Speed and multimodal',
  anthropic: 'Structured reasoning',
  moonshot: 'Long-horizon agent reasoning',
  meta: 'Meta frontier agent model',
  xai: 'Creative generation',
  bfl: 'Precision visual edits',
  qwen: 'Stable instruction follow',
  seed: 'Photoreal rendering',
  ideogram: 'Accurate text and layout',
  unknown: 'General purpose',
};
const getModelPowerHint = (modelId: string): string => {
  return MODEL_POWER_HINTS[modelId] || PROVIDER_POWER_HINTS[getModelProvider(modelId)] || 'General purpose';
};

interface ChatTabProps {
  className?: string;
  /** Render the composer as a ChatGPT-style rounded pill (Master surface). */
  chatGptComposer?: boolean;
}

type ChatComposerProps = {
  placeholderText: string;
  loading: boolean;
  isSubmittingAITask: boolean;
  audioEnabled: boolean;
  isSessionActive: boolean;
  messagesLength: number;
  chatState: ChatStateItem[];
  runningScriptId: string | null;
  isStopRequestedByUI: boolean;
  voiceInputLanguage: string;
  setVoiceInputLanguage: React.Dispatch<React.SetStateAction<string>>;
  modelId: string;
  onOpenModelSelector: () => void;
  /** hide the inline model pill, the Analyst exposes model choice via the "+" menu instead */
  hideModelPill?: boolean;
  /** Render the composer as a ChatGPT-style rounded pill (Master surface). */
  chatGptComposer?: boolean;
};

const ChatComposer: React.FC<ChatComposerProps> = React.memo(({
  placeholderText,
  loading,
  isSubmittingAITask,
  audioEnabled,
  isSessionActive,
  messagesLength,
  chatState,
  runningScriptId,
  isStopRequestedByUI,
  voiceInputLanguage,
  setVoiceInputLanguage,
  modelId,
  onOpenModelSelector,
  hideModelPill,
  chatGptComposer,
}) => {
  const { kernel } = useKernel();
  const chatPrompt = useKernelStore((state) => state.chatPrompt);
  const composerSessionId = useKernelStore((state) => state.activeChatSessionId);
  const composerUserId = useKernelStore((state) => state.userContext?.dbUserId ?? '');
  // Not in a Source conversation, whatever studioViewMode was left at (see ListModeChip).
  const isListSurface = useKernelStore((state) => {
    const id = state.activeChatSessionId;
    return state.studioViewMode === 'marketing' && resolveSourceAgentMode(id ? state.inventory.ChatSession.registry[id] : null) === null;
  });
  // Every module's composer is ChatGPT's (owner, 2026-09-28: Source's first, then 'use what we did
  // on the source chat input and apply it' to the Studio editor); only the Master pill differs.
  const sourceComposer = !chatGptComposer;
  const imagesInChatInput = useKernelStore((state) => state.imagesInChatInput);
  const isQueuingMessage = useKernelStore((state) => state.isQueuingChatMessage);
  const genericImageInputRef = useRef<GenericImageInputHandle>(null);
  const plusButtonRef = useRef<HTMLButtonElement>(null);
  const [isFocused, setIsFocused] = useState(false);
  // Master (chatGptComposer) uses its own ChatGPT-style + popover instead of the
  // shared QuickMenu, so Studio/Source/Analyst context menus are untouched.
  const [plusOpen, setPlusOpen] = useState(false);
  const plusWrapRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!plusOpen) return;
    const onDown = (e: MouseEvent) => {
      if (plusWrapRef.current && !plusWrapRef.current.contains(e.target as Node)) setPlusOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setPlusOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [plusOpen]);
  // Studio "Models" + control: shown whenever the active session belongs to a Studio
  // project, BOTH kinds. Canvas and List generate with the same generative models, and
  // the popover only pins kernel-level `diffusionModel` / `lockSelectedModelForAgent`,
  // so nothing about it is Canvas-specific. Opens the Awen-style generative-model
  // popover; a blue dot marks a pinned model.
  const studioProjectId = useKernelStore((state) => {
    const id = state.activeChatSessionId;
    const session = id ? state.inventory.ChatSession.registry[id] : null;
    if (!session) return null;
    const pid = resolveProjectIdFromChatSessionContext(session);
    if (!pid) return null;
    return state.inventory.Project.registry[pid] ? pid : null;
  });
  const studioProjectType = useKernelStore((state) => (
    studioProjectId ? state.inventory.Project.registry[studioProjectId]?.projectType ?? null : null
  ));
  const studioModelPinned = useKernelStore((state) => state.userPreferences.lockSelectedModelForAgent === true);
  const selectedGenerationModel = useKernelStore((state) => state.diffusionModel);
  const [modelsOpen, setModelsOpen] = useState(false);
  useEffect(() => {
    if (!shouldUnlockIncompatibleStudioModelPin(studioModelPinned, selectedGenerationModel, studioProjectType)) return;
    const k = kernel.current;
    if (!k) return;
    void k.updateAndSaveUserPreference('lockSelectedModelForAgent', false);
  }, [kernel, selectedGenerationModel, studioModelPinned, studioProjectType]);
  // The popover portals to document.body (the composer card clips overflow), so it handles
  // its own backdrop/Escape; the composer only tracks the anchor button element.
  const modelsAnchorRef = useRef<HTMLButtonElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const listEditorRef = useRef<ListPromptEditorHandle>(null);
  const resolveFactoryWorkspaceId = useCallback(() => (
    studioProjectId ? kernel.current?.getProjectUIWorkspaceId(studioProjectId) ?? '' : ''
  ), [kernel, studioProjectId]);
  const factoryArchetypeMention = useFactoryArchetypeMention(listEditorRef, isListSurface, resolveFactoryWorkspaceId, studioProjectId ?? '');
  const brandMention = useBrandMention(textareaRef, !isListSurface);
  const { refreshMention, handleMentionKeyDown, mentionMenu, closeMention } = isListSurface ? factoryArchetypeMention : brandMention;
  const textareaMetricsRef = useRef<{
    minHeight: number;
    maxHeight: number;
    isCalculated: boolean;
    lastAppliedHeight: number | null;
  }>({ minHeight: 0, maxHeight: 0, isCalculated: false, lastAppliedHeight: null });

  const hasImagesInChatInput = imagesInChatInput.length > 0;
  const hasContent = chatPrompt.trim().length > 0 || hasImagesInChatInput;
  const canSend = useMemo(() => {
    if (loading) return false;
    if (isSessionActive) {
      return hasContent || messagesLength > 0;
    }
    return hasContent;
  }, [isSubmittingAITask, loading, hasContent, isSessionActive, messagesLength]);

  const calculateTextareaMetrics = useCallback(() => {
    if (textareaRef.current && (!textareaMetricsRef.current.isCalculated)) {
      const ta = textareaRef.current;
      const computedStyle = getComputedStyle(ta);
      const lineHeightStr = computedStyle.lineHeight;
      const paddingTopStr = computedStyle.paddingTop;
      const paddingBottomStr = computedStyle.paddingBottom;
      const fontSizeStr = computedStyle.fontSize;
      const cssMinHeightStr = computedStyle.minHeight;

      let lineHeight = parseFloat(lineHeightStr);
      if (isNaN(lineHeight) && lineHeightStr === 'normal') {
        const fontSize = parseFloat(fontSizeStr) || 12;
        lineHeight = Math.round(fontSize * 1.2);
      } else if (isNaN(lineHeight)) {
        const fontSize = parseFloat(fontSizeStr) || 12;
        lineHeight = Math.round(fontSize * 1.4);
      }

      const paddingTop = parseFloat(paddingTopStr) || 0;
      const paddingBottom = parseFloat(paddingBottomStr) || 0;
      const totalVerticalPadding = paddingTop + paddingBottom;
      const singleLineContentHeight = lineHeight;

      const cssMinHeight = parseFloat(cssMinHeightStr) || 0;
      const calculatedMinContentHeight = singleLineContentHeight + totalVerticalPadding;

      textareaMetricsRef.current.minHeight = Math.max(cssMinHeight, calculatedMinContentHeight, singleLineContentHeight);

      const maxLines = 9;
      textareaMetricsRef.current.maxHeight = (singleLineContentHeight * maxLines) + totalVerticalPadding;
      textareaMetricsRef.current.isCalculated = true;
    }
  }, []);

  useEffect(() => {
    const ta = textareaRef.current;
    if (!ta) return;

    if (!textareaMetricsRef.current.isCalculated) {
      calculateTextareaMetrics();
    }
    const { minHeight, maxHeight } = textareaMetricsRef.current;
    const originalScrollTop = ta.scrollTop;

    ta.style.height = 'auto';
    let targetHeight = ta.scrollHeight;

    if (chatPrompt.trim() === '' || targetHeight < minHeight) {
      targetHeight = minHeight;
    }

    if (targetHeight >= maxHeight) {
      ta.style.height = `${maxHeight}px`;
      ta.style.overflowY = 'auto';
      if (ta.scrollHeight > maxHeight) {
        ta.scrollTop = originalScrollTop;
      }
    } else {
      ta.style.height = `${targetHeight}px`;
      ta.style.overflowY = 'hidden';
    }

    textareaMetricsRef.current.lastAppliedHeight = parseFloat(ta.style.height);
  }, [chatPrompt, calculateTextareaMetrics]);

  useEffect(() => {
    calculateTextareaMetrics();
    if (textareaRef.current && textareaMetricsRef.current.isCalculated) {
      const timeoutId = setTimeout(() => {
        if (textareaRef.current) {
          const ta = textareaRef.current;
          const { minHeight, maxHeight } = textareaMetricsRef.current;

          ta.style.height = 'auto';
          let newHeight = ta.scrollHeight;

          if (ta.value.trim() === '' || newHeight < minHeight) {
            newHeight = minHeight;
          }

          if (newHeight >= maxHeight) {
            ta.style.height = `${maxHeight}px`;
            ta.style.overflowY = 'auto';
          } else {
            ta.style.height = `${newHeight}px`;
            ta.style.overflowY = 'hidden';
          }
          textareaMetricsRef.current.lastAppliedHeight = parseFloat(ta.style.height);
        }
      }, 0);
      return () => clearTimeout(timeoutId);
    }
  }, [calculateTextareaMetrics]);

  const handlePromptInputChange = useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    kernel.current?.setChatPrompt(e.target.value);
    // The "@" mention tracks the caret, and the caret has just moved with this edit.
    refreshMention();
  }, [kernel, refreshMention]);
  const handlePromptValueReplace = useCallback((nextPrompt: string) => {
    kernel.current?.setChatPrompt(nextPrompt);
    if (listEditorRef.current) requestAnimationFrame(() => listEditorRef.current?.focus());
  }, [kernel]);
  const hasPromptRefs = useMemo(() => hasPromptReferenceTokens(chatPrompt), [chatPrompt]);

  const handleSendPrompt = useCallback(() => {
    void kernel.current?.submitChatPrompt();
  }, [kernel]);
  const handleQueueUserMessage = useCallback(() => {
    void kernel.current?.queueChatUserMessage();
  }, [kernel]);
  const handleImagesChange = useCallback((files: LockableFile[]) => {
    kernel.current?.setImagesInChatInput(files);
  }, [kernel]);
  const handleImagePreviewClick = useCallback((_imageUrl: string, index: number, sourceRect: DOMRect | null) => {
    if (!kernel.current) return;
    kernel.current.openImageCropForLockableFiles(
      kernel.current.getImagesInChatInput(),
      kernel.current.setImagesInChatInput,
      index,
      sourceRect,
      { instruction: null }
    );
  }, [kernel]);
  const handlePlusButtonClick = useCallback(() => {
    kernel.current?.openChatComposerAttachMenu({
      anchorElement: plusButtonRef.current,
      importLabel: 'Add photos & files',
      onImportFiles: () => genericImageInputRef.current?.triggerFileInput(),
      importLogEvent: 'Chat Import File from Menu Clicked',
      menuLogEvent: 'Chat Plus Button Clicked for Context Menu',
    });
  }, [kernel]);

  // "/" menu. Open state is DERIVED from the prompt rather than held: the prompt lives in the
  // kernel, not in local state, so a separate open flag would drift out of step with the text the
  // moment anything else wrote to it. Only the highlighted row is local.
  // The menu is the shortcuts PLUS everything the agent can do, assembled here because this is the
  // layer that owns the command graph: the menu module must not import it, or every reader of a
  // list of names drags the whole agent surface in behind it (it did, and the cycle was immediate).
  const agentActionMenu = useMemo(() => buildAgentActionMenu(DEALER_COMMAND_DEFINITIONS), []);
  const planActionMenu = useMemo(() => buildAgentActionMenu(PULSE_COMMAND_DEFINITIONS, 'pulse'), []);
  // A Canvas conversation on the jnn brain: its menu is the Designer's own, not Source's.
  const isJnnDesignerSurface = useKernelStore((state) => designerBrainOfSession(state, state.activeChatSessionId) === 'jnn');
  const isPlanSurface = useKernelStore((state) => {
    const id = state.activeChatSessionId;
    return (id ? state.inventory.ChatSession.registry[id]?.creationModule : null) === 'pulse';
  });
  // ONE MODULE, ONE MENU. In List the menu is List's commands and nothing else.
  //
  // It shipped for an hour as List's commands FOLLOWED BY Source's, and that is worse than untidy:
  // the second half is a leasing manager's vocabulary (/refine, /fill-stores, /interesting) plus the
  // leasing agent's own action registry, and picking one of those in a Studio List session dispatches
  // a leasing action with no campaign, no shortlist and no portfolio behind it. A menu that offers a
  // command is a promise that it works here.
  //
  // `studioViewMode` is what the portal already sets to 'marketing' for a LIST project, so there is
  // no second notion of "which module am I in" to keep in step with this one.
  const slashMatches = useMemo(
    () => (isListSurface
      ? matchListSlashCommands(chatPrompt)
      : isPlanSurface
        ? matchPlanSlashCommands(chatPrompt, planActionMenu)
        : isJnnDesignerSurface
          ? matchDesignerSlashCommands(chatPrompt)
          : matchSourceSlashCommands(chatPrompt, agentActionMenu)),
    [chatPrompt, agentActionMenu, planActionMenu, isListSurface, isPlanSurface, isJnnDesignerSurface],
  );
  // DERIVED, NOT SYNCED. See the note in portal/ChatInput.tsx: this was one nested state update per
  // keystroke, in both composers, and React throws "Maximum update depth exceeded" past its nested
  // update limit - measured on plain typing on 2026-08-16, with no slash menu open.
  const [slashSelection, setSlashSelection] = useState<{ prompt: string; index: number }>({ prompt: '', index: 0 });
  const slashIndex = slashSelection.prompt === chatPrompt ? slashSelection.index : 0;
  const setSlashIndex = useCallback(
    (next: number | ((current: number) => number)) => {
      setSlashSelection((current) => {
        const base = current.prompt === chatPrompt ? current.index : 0;
        return { prompt: chatPrompt, index: typeof next === 'function' ? next(base) : next };
      });
    },
    [chatPrompt],
  );

  // Type the command and STOP. The user gets to add their own context before sending
  // ("/refine but keep La Caverne top three"), which is the whole point of a command palette
  // over a button. Submitting here would also paste the command's meaning into the transcript,
  // which is what made the chat unreadable.
  // Takes any menu entry, whichever half it came from: only the name is used.
  const pickSlashCommand = useCallback((command: { name: string }) => {
    const prompt = command.name === 'archetype' ? '/archetype: ' : `/${command.name} `;
    const editor = listEditorRef.current;
    if (editor) {
      editor.replace(0, editor.read().text.length, prompt);
      editor.focus();
    } else kernel.current?.setChatPrompt(prompt);
  }, [kernel]);

  const handlePromptKeyDown = useCallback((e: React.KeyboardEvent<HTMLElement>) => {
    if (e.nativeEvent.isComposing || e.nativeEvent.keyCode === 229) return;
    if (handleMentionKeyDown(e)) return;
    if (slashMatches && slashMatches.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashIndex((index) => (index + 1) % slashMatches.length);
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashIndex((index) => (index - 1 + slashMatches.length) % slashMatches.length);
        return;
      }
      if (e.key === 'Escape') {
        e.preventDefault();
        kernel.current?.setChatPrompt('');
        return;
      }
      if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        const chosen = slashMatches[Math.min(slashIndex, slashMatches.length - 1)];
        if (chosen) pickSlashCommand(chosen);
        return;
      }
    }
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (isQueuingMessage) return;

      if (loading) {
        if (chatPrompt.trim().length > 0 || imagesInChatInput.length > 0) {
          handleQueueUserMessage();
        }
      } else {
        handleSendPrompt();
      }
    }
  }, [isQueuingMessage, loading, chatPrompt, imagesInChatInput, handleQueueUserMessage, handleSendPrompt, slashMatches, slashIndex, pickSlashCommand, kernel, handleMentionKeyDown]);

  const handleContainerClick = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) {
      if (listEditorRef.current) listEditorRef.current.focus();
      else textareaRef.current?.focus();
    }
  }, []);

  const handleTranscriptUpdate = useCallback((transcript: string, isFinal: boolean) => {
    if (isFinal && kernel.current && kernel.current.setChatPrompt) {
      const trimmedTranscript = transcript.trim();
      if (trimmedTranscript === '') return;

      const isChineseInput = /^zh(?:-|$)/i.test(voiceInputLanguage);
      kernel.current.setChatPrompt((prevPrompt: string) => {
        const currentActualPrompt = prevPrompt || '';
        if (currentActualPrompt.length === 0) {
          return isChineseInput ? trimmedTranscript : `${trimmedTranscript} `;
        }
        if (isChineseInput) {
          return `${currentActualPrompt}${trimmedTranscript}`;
        }
        const withSeparator = currentActualPrompt.endsWith(' ')
          ? currentActualPrompt
          : `${currentActualPrompt} `;
        return `${withSeparator}${trimmedTranscript} `;
      });

      if (listEditorRef.current) {
        requestAnimationFrame(() => {
          listEditorRef.current?.focus();
          const end = listEditorRef.current?.read().text.length ?? 0;
          listEditorRef.current?.select(end, end);
        });
      } else if (textareaRef.current) {
        textareaRef.current.focus();
        setTimeout(() => {
          if (textareaRef.current) {
            const finalLength = textareaRef.current.value.length;
            textareaRef.current.selectionStart = finalLength;
            textareaRef.current.selectionEnd = finalLength;
          }
        }, 50);
      }
    }
  }, [kernel, voiceInputLanguage]);

  // Master (chatGptComposer): a single-row 52px pill matching ChatGPT, padding +
  // min-height are set inline below so the empty state is exactly 52px. Non-master
  // keeps the two-row card.
  const composerCardClass = chatGptComposer
    ? 'rounded-[28px] bg-[#1B1B1B] text-white'
    : sourceComposer
      // One 8px inset on every side, so the send arrow sits as far from the right rim as from the
      // bottom one. No top padding: the textarea's own 8px plus the card's 8px child gap match it.
      ? 'rounded-[28px] border border-[#262626] bg-[#1B1B1B] px-2 pb-2 pt-0 text-white'
      : 'rounded-[14px] bg-neutral-800 p-2 text-white';
  const controlButtonClass = chatGptComposer || sourceComposer
    ? 'flex h-9 w-9 items-center justify-center rounded-full text-white hover:bg-[#2E2E2E] transition-colors duration-150 disabled:opacity-60'
    : 'p-2 rounded-lg hover:bg-neutral-700/60 transition-colors duration-150 text-neutral-500 hover:text-neutral-400 disabled:opacity-60';
  const plusButtonClass = chatGptComposer || sourceComposer
    ? 'flex h-9 w-9 items-center justify-center rounded-full text-white hover:bg-[#2E2E2E] transition-colors duration-150 disabled:opacity-60'
    : 'flex h-6 w-6 items-center justify-center text-neutral-600 hover:text-neutral-500 transition-colors duration-150 disabled:opacity-60';
  const textareaClass = sourceComposer
    // px-2.5: the text starts on the + glyph's left edge (8px card + 10px, the glyph's inset in its 36px button).
    ? 'block w-full bg-transparent text-white placeholder:text-[#8E8E8E] px-2.5 py-2 resize-none focus:outline-none custom-scrollbar'
    : chatGptComposer
    ? 'block w-full bg-transparent text-white placeholder:text-[#8E8E8E] px-1 py-2 resize-none focus:outline-none custom-scrollbar'
    : 'w-full bg-transparent text-white placeholder:text-neutral-600 px-1 py-2 text-sm resize-none focus:outline-none custom-scrollbar';

  // Shared controls, so the master (single-row pill) and non-master (two-row card)
  // layouts reuse the exact same elements without duplication.
  const plusButton = (
    <button
      ref={plusButtonRef}
      type="button"
      onClick={handlePlusButtonClick}
      className={plusButtonClass}
      aria-label="More attach options"
      data-factory-tour={isListSurface ? 'attach' : undefined}
      disabled={isQueuingMessage}
    >
      <MemoizedPlus size={16} strokeWidth={2.3} />
    </button>
  );
  // Master + control: a ChatGPT-style popover (panel #212121, radius 16, rows with
  // icon + label + description) anchored above the + button. Toggles open/closed on
  // click; closes on outside-click / Escape / item select.
  const masterPlusControl = (
    <div ref={plusWrapRef} className="relative shrink-0">
      <button
        ref={plusButtonRef}
        type="button"
        onClick={() => setPlusOpen((o) => !o)}
        className={plusButtonClass}
        aria-label="Add photos & files"
        data-factory-tour={isListSurface ? 'attach' : undefined}
        aria-haspopup="menu"
        aria-expanded={plusOpen}
        disabled={isQueuingMessage}
      >
        <MemoizedPlus size={16} strokeWidth={2.3} />
      </button>
      {plusOpen && (
        <div
          role="menu"
          style={{
            position: 'absolute',
            bottom: 'calc(100% + 8px)',
            left: 0,
            zIndex: 1000,
            minWidth: 300,
            background: '#353535',
            border: '1px solid #2A2A2A',
            borderRadius: 16,
            padding: '6px',
            boxShadow: '0 18px 52px rgba(0, 0, 0, 0.55)',
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              genericImageInputRef.current?.triggerFileInput();
              kernel.current?.logUserAction('Chat Import File from Menu Clicked');
              setPlusOpen(false);
            }}
            className="flex w-full items-center gap-2.5 rounded-[10px] px-2.5 py-2 text-left transition-colors duration-150 hover:bg-[#2E2E2E]"
          >
            <Paperclip size={16} className="shrink-0 text-[#B5B5B5]" />
            <span className="whitespace-nowrap text-[14px] text-[#ECECEC]">Add photos &amp; files</span>
            <span className="ml-3 whitespace-nowrap text-[12px] text-[#8E8E8E]">Upload from computer</span>
          </button>
        </div>
      )}
    </div>
  );
  // Studio-only Models control (CPU chip icon, Awen-style popover): pick the GENERATIVE
  // model (diffusion / video / 3D), never the LLM, which is Fater's backend choice. The
  // blue dot mirrors Awen's: it marks that a model is pinned (Auto routing off).
  const studioModelsControl = studioProjectId ? (
    <div className="relative shrink-0">
      <button
        ref={modelsAnchorRef}
        type="button"
        onClick={() => setModelsOpen((o) => !o)}
        className={clsx(plusButtonClass, 'relative')}
        aria-label="Select generation model"
        data-factory-tour={isListSurface ? 'generation-model' : undefined}
        aria-haspopup="dialog"
        aria-expanded={modelsOpen}
        title="Models"
        disabled={isQueuingMessage}
      >
        <Cpu size={16} strokeWidth={2} />
        {studioModelPinned && (
          <span className="absolute right-0 top-0 h-1.5 w-1.5 rounded-full bg-[#1E50C8]" />
        )}
      </button>
      {modelsOpen && (
        <StudioModelsPopover anchorEl={modelsAnchorRef.current} onClose={() => setModelsOpen(false)} />
      )}
    </div>
  ) : null;
  const voiceButton = (
    <VoiceInputButton
      onTranscriptUpdate={handleTranscriptUpdate}
      disabled={loading || isSubmittingAITask || !audioEnabled || isQueuingMessage}
      className={controlButtonClass}
      iconSize={sourceComposer ? 18 : 14}
      iconColor="currentColor"
      currentLanguage={voiceInputLanguage}
      onSetLanguage={setVoiceInputLanguage}
    />
  );
  const sendButton = (
    <ChatActionButton
      chatState={chatState}
      isRunningScript={!!(runningScriptId && runningScriptId.length > 0)}
      isStopRequestedByUI={isStopRequestedByUI}
      canSendMessage={canSend}
      onSend={handleSendPrompt}
      isQueuingMessage={isQueuingMessage}
      pill={sourceComposer ? 'blue' : chatGptComposer}
    />
  );
  const textareaBlock = (
    <div className={clsx('relative', chatGptComposer && 'min-w-0 flex-1')}>
      {mentionMenu}
      {slashMatches && slashMatches.length > 0 ? (
        // Sits in normal flow above the textarea rather than portalled: it is part of the composer
        // card, not an overlay anchored to a chip, so the card grows to fit it and nothing clips.
        <SlashCommandMenu
          commands={slashMatches}
          activeIndex={slashIndex}
          groupLabel={(group) => SLASH_GROUP_LABELS[group ?? 'source']}
          onPick={pickSlashCommand}
          onHover={setSlashIndex}
        />
      ) : null}
      {/* Inset so its pill sits concentric in the card's 28px corner (about 17px down, with the
          card's 8px gap) and starts on the text's left edge, the + glyph's, not jammed in the curve. */}
      {isListSurface ? <ListPromptEditor
        key={`${composerUserId}:${studioProjectId}:${composerSessionId}`}
        scope={`${composerUserId}:${studioProjectId}:${composerSessionId}`}
        editorRef={listEditorRef}
        projectId={studioProjectId ?? ''}
        sessionId={composerSessionId ?? ''}
        placeholder={isFocused ? '' : placeholderText}
        disabled={isQueuingMessage}
        className={textareaClass}
        onKeyDown={handlePromptKeyDown}
        onSelectionChange={factoryArchetypeMention.refreshMention}
        onFocus={() => setIsFocused(true)}
        onBlur={event => { setIsFocused(false); closeMention(event.relatedTarget); }}
      /> : <textarea
        ref={textareaRef}
        data-assistant-composer=""
        rows={1}
        value={chatPrompt}
        onChange={handlePromptInputChange}
        onKeyDown={handlePromptKeyDown}
        placeholder={isFocused ? '' : (chatGptComposer ? 'Ask anything' : placeholderText)}
        onFocus={() => setIsFocused(true)}
        onBlur={event => { setIsFocused(false); closeMention(event.relatedTarget); }}
        onKeyUp={refreshMention}
        onClick={refreshMention}
        className={textareaClass}
        style={{
          minHeight: chatGptComposer ? '40px' : '2.25rem',
          height: textareaMetricsRef.current.isCalculated ? `${textareaMetricsRef.current.minHeight}px` : (chatGptComposer ? '40px' : '2.25rem'),
          overflowY: 'hidden',
          ...(chatGptComposer ? { fontSize: '16px', lineHeight: '26px' } : sourceComposer ? { fontSize: '14px', lineHeight: '22px' } : {}),
          ...(hasPromptRefs && !isFocused ? {
            color: 'transparent',
            WebkitTextFillColor: 'transparent',
            caretColor: '#ffffff',
          } : {}),
        }}
        disabled={isQueuingMessage}
      />}
      {hasPromptRefs && (
        <PromptReferenceTagCards
          prompt={chatPrompt}
          imageReferences={imagesInChatInput}
          onPromptChange={handlePromptValueReplace}
          textareaRef={textareaRef}
          tokensOnly={isListSurface}
          className={isListSurface ? 'relative mt-2 text-sm text-white' : 'pointer-events-none absolute inset-0 px-1 py-2 text-sm text-white'}
        />
      )}
    </div>
  );

  return (
    <>
    {/* Module quick-actions sit ABOVE the composer card, not inside it: this is
        a way to start a run, not a field of the message you are writing. It
        renders nothing outside a Slides session, and carries its OWN bottom
        margin, a wrapper here would leave an empty spacer above every
        non-Slides composer. */}
    <SlidesReviewLauncher />
    <PlanSendToSlides />
    <SourceResumeChip />
    <div
      id="editor-agent-input"
      data-composer-card
      data-slides-tour="activity"
      data-slides-tour-session={composerSessionId ?? undefined}
      data-factory-tour-scope={isListSurface ? 'chat' : undefined}
      className={clsx('relative', chatGptComposer ? '' : 'overflow-hidden space-y-2', composerCardClass)}
      style={chatGptComposer ? { minHeight: '52px', padding: '5px 8px' } : undefined}
      onClick={handleContainerClick}
    >
      <GenericImageInput
        ref={genericImageInputRef}
        displayMode="previewsOnly"
        multiple
        imageFiles={imagesInChatInput}
        onImagesChange={handleImagesChange}
        onImageClick={handleImagePreviewClick}
        minHeight="0px"
        inputStyle={hasImagesInChatInput ? IMAGE_INPUT_STYLE_WITH_PREVIEWS : IMAGE_INPUT_STYLE_EMPTY}
        previewCursor={scissorsCursor}
        showLock={false}
      />

      {chatGptComposer ? (
        // ChatGPT single-row pill: [+] [text] [mic] [send], vertically centered.
        <div className="flex items-center gap-1">
          {masterPlusControl}
          {textareaBlock}
          {voiceButton}
          {sendButton}
        </div>
      ) : (
        <>
          {textareaBlock}
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1">
              {plusButton}
              {studioModelsControl}
              {/* Renders nothing outside a Source session; it asks the session itself, so Studio,
                  Slides, Analyst and the global chat are untouched by its presence here. */}
              <SourceModeChip disabled={isQueuingMessage} />
              <ListModeChip disabled={isQueuingMessage} />
              <DesignerBudgetChip disabled={isQueuingMessage} />
              {!hideModelPill && (
                <button
                  type="button"
                  onClick={onOpenModelSelector}
                  className="flex items-center gap-1 rounded-md px-1.5 py-1 text-[12px] text-neutral-400 hover:bg-neutral-700/60 hover:text-neutral-200 transition-colors duration-150 max-w-[180px]"
                  title="Active model, click to change"
                  aria-label="Select model"
                >
                  <ProviderModelIcon modelId={modelId} size={12} />
                  <span className="truncate">{getModelDisplayName(modelId)}</span>
                  <ChevronDown size={12} className="shrink-0 text-neutral-500" />
                </button>
              )}
            </div>

            <div className="flex items-center gap-1">
              {voiceButton}
              {sendButton}
            </div>
          </div>
        </>
      )}
    </div>
    </>
  );
});

ChatComposer.displayName = 'ChatComposer';

// Owns its draft state so typing re-renders only this panel, not ChatTab + <Chat>.
const ChatCustomizationPanel: React.FC<{ baselinePrompt: string; hidden?: boolean }> = React.memo(({
  baselinePrompt,
  hidden,
}) => {
  const { kernel } = useKernel();
  const [draft, setDraft] = useState(baselinePrompt);
  const [isSaving, setIsSaving] = useState(false);
  const hasChanges = draft !== baselinePrompt;

  const handleSave = useCallback(async () => {
    if (!kernel.current || isSaving) return;
    setIsSaving(true);
    try {
      await kernel.current.updateAndSaveUserPreference('assistantGlobalPrompt', draft);
      kernel.current.logUserAction('Assistant Global Prompt Updated', {
        length: draft.length,
        source: 'chat_model_selector_modal',
      });
    } finally {
      setIsSaving(false);
    }
  }, [kernel, draft, isSaving]);

  return (
    <div className={clsx('space-y-3', hidden && 'hidden')}>
      <div className="px-1">
        <div className="text-[12px] text-white/90">Custom agent instructions</div>
        <div className="mt-1 text-[11px] text-white/45">
          Global guidance applied to the assistant in your chats.
        </div>
      </div>
      <textarea
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        placeholder="e.g. Keep answers concise, prioritize concrete actions, ask before destructive changes."
        className="min-h-[180px] w-full resize-y rounded-[10px] border border-white/10 bg-black/20 px-3 py-2 text-[12px] text-white/90 placeholder:text-white/35 focus:outline-none focus:ring-1 focus:ring-[#B3D9FF]/50"
      />
      <div className="flex items-center justify-between gap-3">
        <div className="text-[10px] text-white/40">
          {draft.length} characters
        </div>
        <button
          type="button"
          onClick={() => { void handleSave(); }}
          disabled={!hasChanges || isSaving}
          className={clsx(
            'rounded-[9px] px-3 py-1.5 text-[12px] transition-colors',
            (!hasChanges || isSaving)
              ? 'bg-white/10 text-white/40 cursor-not-allowed'
              : 'bg-[#B3D9FF] text-black hover:bg-[#a0d0ff]'
          )}
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </div>
  );
});

ChatCustomizationPanel.displayName = 'ChatCustomizationPanel';

const ChatTab: React.FC<ChatTabProps> = React.memo(({
  className,
  chatGptComposer = false,
}) => {
  const { kernel } = useKernel();
  // A suspended account keeps its work visible but cannot talk to the agent. The clock is
  // captured once (Date.now() is not allowed during render); the organisation is compared on
  // every render because it loads asynchronously after mount.
  const suspensionOrganisationId = useKernelStore((state) => state.userContext.organisationId);
  const suspensionTeamId = useKernelStore((state) => state.userContext.teamId);
  const serverSuspension = useKernelStore((state) => state.userContext.accessSuspension);
  const [suspensionNow] = useState(() => Date.now());
  // The server's verdict wins as soon as it lands: the tables in this bundle are as old as the tab.
  const accountSuspended = clientAccessSuspension(
    serverSuspension,
    suspensionOrganisationId,
    suspensionTeamId,
    suspensionNow,
  ).suspended;
  const activeChatSessionId = useKernelStore((state) => state.activeChatSessionId);
  // A Source session reads like ChatGPT, as Master does (owner, 2026-09-28): the same test the
  // composer and SourceModeChip use, so every other module keeps its chat.
  const sourceSurface = useKernelStore((state) => {
    const id = state.activeChatSessionId;
    return resolveSourceAgentMode(id ? state.inventory.ChatSession.registry[id] : null) !== null;
  });
  // By id, not the whole registry: avoids re-rendering on every session's runtime churn.
  const activeSession = useKernelStore((state) =>
    activeChatSessionId ? state.inventory.ChatSession.registry[activeChatSessionId] ?? null : null
  );
  // Only the empty-state list needs the whole registry; stays null while a session is active.
  const chatSessionsRegistry = useKernelStore((state) =>
    activeChatSessionId ? null : state.inventory.ChatSession.registry
  );
  const audioEnabled = useKernelStore((state) => state.audioEnabled);
  const isSubmittingAITask = useKernelStore((state) => state.isSubmittingAITask);
  const userPreferences = useKernelStore((state) => state.userPreferences);
  const isFaterOrgMember = useKernelStore((state) => state.userContext.isFaterOrgMember);
  const resolvedOrganisationDetails = useKernelStore((state) => {
    const orgId = state.userContext.organisationId;
    return orgId ? (state.inventory.Organisation.registry[orgId] as LeasingOrganisationDTO) : null;
  });
  const storeAssistantModel = useKernelStore((state) => state.userPreferences.assistantModel);
  const chatSessions = useMemo(
    () => (chatSessionsRegistry ? Object.values(chatSessionsRegistry) : []),
    [chatSessionsRegistry]
  );
  const resolvedAssistantModel = storeAssistantModel ?? MAIN_TEXT_MODEL;
  const sessionRuntime = activeSession?.runtime;
  const chatState = sessionRuntime?.chatState ?? [];
  const runningScriptId = sessionRuntime?.runningScriptId ?? null;
  const isStopRequestedByUI = sessionRuntime?.isStopRequested === true;

  const visibleChatSessions = useMemo(() => {
    const leasingSessions = chatSessions
      .filter((session) => !resolveProjectIdFromChatSessionContext(session))
      .sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.createdAt).getTime();
        const dateB = new Date(b.updatedAt || b.createdAt).getTime();
        return dateB - dateA;
      });
    if (!activeChatSessionId) return leasingSessions;
    const activeSession = chatSessions.find((s) => s.id === activeChatSessionId);
    if (activeSession && !leasingSessions.some((s) => s.id === activeSession.id)) {
      return [activeSession, ...leasingSessions];
    }
    return leasingSessions;
  }, [chatSessions, activeChatSessionId]);

  const permissionSessionId = useKernelStore((state) => state.activeChatSessionId);
  const listPermission = useKernelStore(selectListPermission(permissionSessionId));
  const messages = useSessionMessages(activeChatSessionId);
  const messagesLength = messages.length;
  const budgetRuntime = useKernelStore(state => state.sourceBudgetProposals);
  const budgetCampaignId = useKernelStore(state => state.sourceSelectedCampaignId);
  const historyBudgetProposals = useMemo(() => sourceSurface ? messages.flatMap(message => (message.scriptsInstances ?? []).flatMap(script =>
      script.commands.flatMap(command => {
        const proposal = command.action_type === 'PROPOSE_SOURCING_BUDGET' && command.status === 'success'
          ? readSourceBudgetProposal(command.parameters.result) : null;
        return proposal ? [proposal] : [];
      }))) : [], [messages, sourceSurface]);
  const latestBudgetProposal = useMemo(() => {
    if (!sourceSurface) return undefined;
    const proposals = [...historyBudgetProposals];
    const historyOrder = new Map(proposals.map((proposal, index) => [proposal.id, index]));
    for (const runtime of Object.values(budgetRuntime)) if (runtime.proposal && !historyOrder.has(runtime.proposal.id)) proposals.push(runtime.proposal);
    return proposals.filter(proposal => proposal.chatSessionId === activeChatSessionId && proposal.campaignId === budgetCampaignId)
      .sort((a, b) => Date.parse(b.expiresAt) - Date.parse(a.expiresAt)
        || (historyOrder.get(b.id) ?? messages.length) - (historyOrder.get(a.id) ?? messages.length))[0];
  }, [historyBudgetProposals, budgetRuntime, activeChatSessionId, budgetCampaignId, sourceSurface]);
  // A proposal the mandate posted itself (a widened brief once the shortlist is spent) is no command of
  // this conversation: looked up whenever a message lands on an open shortlist.
  // And again in the minute after the run lands: the mandate measures its widening first, and a proposal posted
  // then must not wait for a reload (owner, 2026-10-14: "it didn't give me the proposal").
  const budgetCampaignStatus = useKernelStore(state => budgetCampaignId ? (state.inventory.Campaign.registry[budgetCampaignId] as { status?: string } | undefined)?.status : undefined);
  useEffect(() => {
    if (!sourceSurface || !activeChatSessionId || !budgetCampaignId) return;
    const look = () => void kernel.current?.sourceBudgetProposal(budgetCampaignId, { action: 'latest', chatSessionId: activeChatSessionId }).catch(() => undefined);
    look();
    if (budgetCampaignStatus !== 'COMPLETE') return;
    const timers = [20_000, 45_000, 90_000].map((ms) => setTimeout(look, ms));
    return () => timers.forEach(clearTimeout);
  }, [sourceSurface, activeChatSessionId, budgetCampaignId, messagesLength, budgetCampaignStatus]);
  const [budgetClock, setBudgetClock] = useState(Date.now);
  useEffect(() => {
    if (!latestBudgetProposal) return;
    const timer = setTimeout(() => setBudgetClock(Date.now()), Math.max(0, Date.parse(latestBudgetProposal.expiresAt) - Date.now()));
    return () => clearTimeout(timer);
  }, [latestBudgetProposal?.id, latestBudgetProposal?.expiresAt]);
  const pendingBudgetProposal = sourceSurface && latestBudgetProposal
    && Date.parse(latestBudgetProposal.expiresAt) > Math.max(budgetClock, Date.now())
    && (budgetRuntime[latestBudgetProposal.id]?.proposal ?? latestBudgetProposal).status === 'pending'
    ? latestBudgetProposal : null;


  const [voiceInputLanguage, setVoiceInputLanguage] = useState<string>('en-US');
  // The model that will actually run: when a table is open in the Analyst the
  // agent is forced to the Analyst's per-tool default (Claude Opus 4.8 fallback);
  // otherwise it's the user's chosen agent model. The chat pill shows this.
  // Gate EXACTLY like the agent turn does: useAgents only applies the Analyst model when
  // buildAnalystContext() is non-null, which requires BOTH isAnalystOpen and an open doc. If we
  // gated on activeDocId alone, the pill + the persisted pick could diverge from the model the
  // turn actually uses (e.g. a doc still open in the store while the Analyst view is unmounted).
  const analystTableOpen = useTablesStore((state) => state.isAnalystOpen && !!state.activeDocId);
  // For the Analyst the pill reflects the model actually used server-side: the user's pinned
  // toolDefaultModels.ANALYST if set, otherwise resolveAnalystModel's default. For other tools it shows
  // the chat selection.
  const effectiveAssistantModel = useMemo(
    () => {
      if (!analystTableOpen) return resolvedAssistantModel;
      return userPreferences?.toolDefaultModels?.ANALYST || MAIN_TEXT_MODEL;
    },
    [analystTableOpen, resolvedAssistantModel, userPreferences?.toolDefaultModels],
  );
  const allUnifiedModelIds = useMemo(() => {
    // Ultramarine is on trial in Analyst only.
    return filterAgentModelIdsForContext(Array.from(new Set([...AGENT_LLMS])), { isFaterOrgMember }).filter((id) => analystTableOpen || id !== ANALYST_TRIAL_MODEL);
  }, [isFaterOrgMember, analystTableOpen]);
  const availableAgentModelIds = useMemo(() => {
    return allUnifiedModelIds
      .filter((id) => typeof id === 'string' && id.trim().length > 0);
  }, [allUnifiedModelIds]);
  const [isModelSelectorModalOpen, setIsModelSelectorModalOpen] = useState(false);
  const [modelSelectorSection, setModelSelectorSection] = useState<'agent' | 'customization'>('agent');
  const [isClient, setIsClient] = useState(false);
  const currentAssistantGlobalPrompt = userPreferences.assistantGlobalPrompt || '';

  // Last payload this tab tried to save, so a failed PUT can't loop: updateAndSaveUserPreference
  // reverts the optimistic store value on failure, which re-triggers the seeding effect with the
  // exact same derived value, which would otherwise retry forever (one failed write + one revert
  // per cycle). Refusing to re-attempt an identical payload turns that into a single failed
  // write. Ref, not state: retry suppression is a write concern, not something to render on.
  const lastAttemptedRecentModelIdsRef = useRef<string | null>(null);
  // Reads/writes the live kernel snapshot (not the reactive `userPreferences` selector above) so
  // this stays a stable callback instead of churning on every write it itself causes.
  const persistRecentUnifiedModels = useCallback((ids: string[]) => {
    const stored = kernel.current?.getUserPreferences()?.recentAssistantModelIds;
    // undefined = truth not known yet (not hydrated, or a failed load): never write over it,
    // that would truncate whatever the server actually holds once it does load.
    if (stored === undefined) return;
    const payload = JSON.stringify(ids);
    if (JSON.stringify(stored) === payload) return;
    if (lastAttemptedRecentModelIdsRef.current === payload) return;
    lastAttemptedRecentModelIdsRef.current = payload;
    void kernel.current?.updateAndSaveUserPreference('recentAssistantModelIds', ids);
  }, [kernel]);

  const trackRecentUnifiedModel = useCallback((modelId: string) => {
    const prev = kernel.current?.getUserPreferences()?.recentAssistantModelIds ?? [];
    const normalized = Array.from(new Set([modelId, ...prev]))
      .filter((id) => allUnifiedModelIds.includes(id));
    persistRecentUnifiedModels(normalized);
  }, [kernel, allUnifiedModelIds, persistRecentUnifiedModels]);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (resolvedOrganisationDetails?.favoriteLanguage === 'FRENCH') {
      setVoiceInputLanguage('fr-FR');
    } else {
      setVoiceInputLanguage('en-US');
    }
  }, [resolvedOrganisationDetails]);

  useEffect(() => {
    // undefined = preferences not hydrated yet: wait rather than seed from empty and
    // overwrite the real server value once it lands.
    const stored = userPreferences.recentAssistantModelIds;
    if (stored === undefined) return;
    const parsed = (Array.isArray(stored) ? stored : []).filter((id) => allUnifiedModelIds.includes(id));
    const seeded = Array.from(new Set([resolvedAssistantModel, ...parsed]))
      .filter((id) => allUnifiedModelIds.includes(id));
    persistRecentUnifiedModels(seeded);
  }, [resolvedAssistantModel, allUnifiedModelIds, userPreferences.recentAssistantModelIds, persistRecentUnifiedModels]);

  const closeModelSelectorModal = useCallback(() => {
    setIsModelSelectorModalOpen(false);
  }, []);

  const openModelSelectorModal = useCallback((section: 'agent' | 'customization' = 'agent') => {
    setModelSelectorSection(section);
    setIsModelSelectorModalOpen(true);
  }, []);

  useEffect(() => {
    if (!isModelSelectorModalOpen) return;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        closeModelSelectorModal();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [isModelSelectorModalOpen, closeModelSelectorModal]);

  const handleUnifiedModelSelect = useCallback(async (modelId: string) => {
    if (!kernel.current) return;
    if (!availableAgentModelIds.includes(modelId)) return;
    const appliesToAgent = availableAgentModelIds.includes(modelId);
    if (appliesToAgent) {
      if (analystTableOpen) {
        // Pin the chosen model as the Analyst's per-tool default so it's actually used server-side
        // (the route reads toolDefaultModels.ANALYST) and shown in the pill. Picking Gemini, Claude or
        // GPT-family models here now switch the Analyst to that model; clearing it falls back to the priority default.
        const currentToolDefaults = { ...(userPreferences.toolDefaultModels ?? {}), ANALYST: modelId };
        await kernel.current.updateAndSaveUserPreference('toolDefaultModels', currentToolDefaults);
      } else {
        await kernel.current.updateAndSaveUserPreference('assistantModel', modelId);
      }
    }
    trackRecentUnifiedModel(modelId);
    kernel.current.logUserAction('Unified Model Selected', {
      model_id: modelId,
      applies_to_agent: appliesToAgent,
      analyst_scope: appliesToAgent && analystTableOpen,
    });
    closeModelSelectorModal();
  }, [kernel, trackRecentUnifiedModel, closeModelSelectorModal, analystTableOpen, userPreferences.toolDefaultModels, availableAgentModelIds]);

  const openAgentModelSelector = useCallback(() => {
    openModelSelectorModal('agent');
  }, [openModelSelectorModal]);

  const isSessionActive = activeChatSessionId !== null;
  const loading = agentIsWorking(chatState, runningScriptId);

  const placeholderText = useMemo(() => {
    if (INSTRUCTION_MODEL_IDS.includes(resolvedAssistantModel)) {
      const mapping = modelMappings[resolvedAssistantModel];
      let modelName = mapping?.name || resolvedAssistantModel;

      if (resolvedAssistantModel === 'seedream4.5') {
        modelName = 'Seedream4.5';
      } else if (resolvedAssistantModel === 'nano-banana') {
        modelName = 'Nano Banana';
      } else if (resolvedAssistantModel === 'nano-banana-pro') {
        modelName = 'Nano Banana Pro';
      }
      
      return `Ask ${modelName}...`;
    }

    return "Ask Fater";
  }, [resolvedAssistantModel]);


  const rootClassName = clsx(
    'chat-tab-root flex h-full w-full flex-col',
    className,
  );

  return (
    <>
      <div
        className={rootClassName}
        style={{
          position: 'relative',
          height: '100%',
          width: '100%',
          color: 'white',
          display: 'flex',
          flexDirection: 'column',
          paddingBottom: '8px',
          boxSizing: 'border-box',
        }}
        tabIndex={0}
        drop-area="imagesInChatInput"
      >
        {accountSuspended && (
          // Sits above the whole panel: it blurs the conversation behind it AND swallows every
          // click, so the composer cannot be reached at all rather than failing on send.
          <div
            className="absolute inset-0 z-40 flex flex-col items-center justify-center gap-3 bg-black/60 px-6 text-center backdrop-blur-md"
            role="status"
          >
            <Lock size={20} className="text-white/55" />
            <p className="max-w-[42ch] text-[12px] leading-relaxed text-white/70">{SUSPENSION_MESSAGE}</p>
          </div>
        )}
        <div className="flex-1 relative min-h-0" data-slides-tour="results" data-slides-tour-session={activeChatSessionId ?? undefined}>
          {activeChatSessionId ? (
            <Chat
              runningScriptId={runningScriptId}
              chatState={chatState}
              chatGptSurface={chatGptComposer || sourceSurface}
            />
          ) : (
            <div className="h-full overflow-y-auto custom-scrollbar p-4 space-y-2">
              {visibleChatSessions.length === 0 ? (
                <div className="text-center text-white/40 text-sm mt-10">
                  No recent chats. Start a new one below.
                </div>
              ) : (
                visibleChatSessions.map((session, index) => (
                  <SessionRow
                    key={session.id}
                    session={session}
                    index={index}
                    compact={true}
                  />
                ))
              )}
            </div>
          )}
        </div>

        {/* Master centers the composer at the content width; the scroll area behind it
            is full-width so the gutters scroll. Other surfaces keep the composer full-width. */}
        <div className={clsx(chatGptComposer && 'flex w-full justify-center')}>
          <div className={clsx('w-full', chatGptComposer && 'max-w-[880px]')}>
            {listPermission ? <ListPermissionPrompt request={listPermission} />
              : pendingBudgetProposal ? <SourceBudgetProposalCard key={pendingBudgetProposal.id} initial={pendingBudgetProposal} approval /> : <ChatComposer
              placeholderText={placeholderText}
              loading={loading}
              isSubmittingAITask={isSubmittingAITask}
              audioEnabled={audioEnabled}
              isSessionActive={isSessionActive}
              messagesLength={messagesLength}
              chatState={chatState}
              runningScriptId={runningScriptId}
              isStopRequestedByUI={isStopRequestedByUI}
              voiceInputLanguage={voiceInputLanguage}
              setVoiceInputLanguage={setVoiceInputLanguage}
              modelId={effectiveAssistantModel}
              onOpenModelSelector={openAgentModelSelector}
              hideModelPill={true}
              chatGptComposer={chatGptComposer}
            />}
          </div>
        </div>
      </div>
      {isClient && isModelSelectorModalOpen && createPortal(
        <div
          className="fixed inset-0 z-[1200] bg-black/55 backdrop-blur-[2px] flex items-center justify-center p-4"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              closeModelSelectorModal();
            }
          }}
        >
          <div className="w-full max-w-2xl rounded-[14px] border border-white/10 bg-neutral-800 text-white shadow-2xl">
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="text-[14px] font-semibold">Model selector</div>
              <button
                type="button"
                onClick={closeModelSelectorModal}
                className="p-1.5 text-white/60 hover:text-white transition-colors"
                aria-label="Close model selector"
              >
                <X size={14} />
              </button>
            </div>
            <div className="grid grid-cols-[170px_1fr] max-h-[68vh]">
              <div className="border-r border-white/10 p-3 space-y-2 bg-black/10">
                <button
                  type="button"
                  onClick={() => setModelSelectorSection('agent')}
                  className={clsx(
                    'w-full rounded-[10px] px-3 py-2 text-left text-[12px] transition-colors',
                    modelSelectorSection === 'agent' ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10'
                  )}
                >
                  Agent
                </button>
                <button
                  type="button"
                  onClick={() => setModelSelectorSection('customization')}
                  className={clsx(
                    'w-full rounded-[10px] px-3 py-2 text-left text-[12px] transition-colors',
                    modelSelectorSection === 'customization' ? 'bg-white/15 text-white' : 'text-white/70 hover:bg-white/10'
                  )}
                >
                  Customization
                </button>
              </div>
              <div className="p-3 space-y-1 overflow-y-auto custom-scrollbar">
                {modelSelectorSection === 'agent' && (
                  <>
                    <div className="px-1 pb-1 text-[11px] text-white/45">Agent models</div>
                    {availableAgentModelIds.map((modelId) => {
                      const label = getModelDisplayName(modelId);
                      // Highlight the model that's actually active: in the Analyst that's the
                      // per-tool ANALYST model (effectiveAssistantModel), elsewhere the global one.
                      const selected = modelId === effectiveAssistantModel;
                      const powerHint = getModelPowerHint(modelId);
                      return (
                        <button
                          key={`agent-${modelId}`}
                          type="button"
                          className={clsx(
                            'w-full rounded-[10px] px-3 py-2 text-left transition-colors',
                            selected ? 'bg-[#B3D9FF1F] ring-1 ring-[#B3D9FF66]' : 'hover:bg-white/10'
                          )}
                          onClick={() => void handleUnifiedModelSelect(modelId)}
                        >
                          <div className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3">
                            <div className="flex min-w-0 items-center gap-2.5">
                              <ProviderModelIcon modelId={modelId} size={14} />
                              <span className={clsx('truncate text-[13px]', selected ? 'text-[#B3D9FF]' : 'text-white/90')}>{label}</span>
                            </div>
                            <div className="max-w-[180px] text-right leading-4">
                              <div className="text-[11px] text-white/45">{powerHint}</div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </>
                )}
                <ChatCustomizationPanel
                  baselinePrompt={currentAssistantGlobalPrompt}
                  hidden={modelSelectorSection !== 'customization'}
                />
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}
    </>
  );
});

ChatTab.displayName = 'ChatTab';
export default ChatTab;
